/**
 * **구독 기간의 날짜 계산** — 모든 경계는 한국 시각(KST) 그날 23:59:59 다.
 *
 * ```
 *   첫 결제   firstPeriodEndMs(결제 시각, 주기)        3/15 13:00 → 4/15 23:59:59 (월) · 다음 해 3/15 (연)
 *            firstPaidPeriod — 보너스를 처음 받는 워크스페이스만 무료 1달을 더한다(워크스페이스당 한 번)
 *              체험 중   오늘 + 한 주기 + 1달 + 남은 체험 N일   (bonusType 'trial')
 *              만료 뒤   오늘 + 한 주기 + 1달                  (bonusType 'winback')
 *   갱신     nextPeriodEndMs(지금 종료일, 주기, 기준일)   기존 종료일에서 이어 붙인다 — 결제가 늦어도 날짜가 밀리지 않는다
 *   일할     periodDays · remainingDays                 남은 일수 ÷ 주기 일수
 * ```
 *
 * 🔑 **말일 규칙** — 그 달에 기준일이 없으면 그달 마지막 날로 당긴다(1/31 → 2/28 → 3/31). 기준일(`anchorDay`)은
 * 첫 결제일의 «일»이고 구독 문서의 `billingAnchorDay` 에 둔다 — 2월에 한 번 당겨져도 다음 달에 31일로 돌아온다.
 * 윤년 2/29 의 연간 갱신은 평년에 2/28 이다.
 * ⚠️ 서버는 UTC 로 돈다 — `Date` 의 지역 시각 메서드를 쓰지 않고 KST 오프셋을 명시한다.
 */

import type { BillingCycle } from './billing-cycle';

/** KST = UTC+9 (서머타임 없음) */
export const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/** KST 달력 날짜 */
export type KstDate = { year: number; month: number; day: number };

/** 시각 → KST 달력 날짜(월은 1~12) */
export function toKstDate(ms: number): KstDate {
  const shifted = new Date(ms + KST_OFFSET_MS);
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() };
}

/** KST 그날 23:59:59.000 의 시각 */
export function kstDayEndMs(date: KstDate): number {
  return Date.UTC(date.year, date.month - 1, date.day, 23, 59, 59, 0) - KST_OFFSET_MS;
}

/** `YYYY-MM-DD`(KST) — 멱등 키·화면 표기 */
export function kstDateKey(ms: number): string {
  const { year, month, day } = toKstDate(ms);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * 날짜에 달을 더한다 — 그달에 기준일이 없으면 말일로 당긴다.
 * @param anchorDay 지킬 «일»(1~31) — 없으면 `date.day`
 */
export function addMonthsKst(date: KstDate, months: number, anchorDay: number = date.day): KstDate {
  const index = date.year * 12 + (date.month - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return { year, month, day: Math.min(anchorDay, daysInMonth(year, month)) };
}

/** 첫 결제의 종료 시각 — 결제한 KST 날짜에서 한 주기 뒤 그날 끝 */
export function firstPeriodEndMs(startMs: number, cycle: BillingCycle): number {
  const start = toKstDate(startMs);
  return kstDayEndMs(addMonthsKst(start, monthsOf(cycle), start.day));
}

/**
 * 갱신의 새 종료 시각 — **지금 종료일**에서 한 주기를 이어 붙인다.
 * @param anchorDay 구독 문서의 `billingAnchorDay` — 없으면 지금 종료일의 «일»
 */
export function nextPeriodEndMs(currentEndMs: number, cycle: BillingCycle, anchorDay?: number | null): number {
  const end = toKstDate(currentEndMs);
  return kstDayEndMs(addMonthsKst(end, monthsOf(cycle), validAnchor(anchorDay) ?? end.day));
}

/** 이번 주기가 시작한 날의 직전 종료 시각 — 종료일에서 한 주기를 거꾸로 뺀다 */
export function previousPeriodEndMs(currentEndMs: number, cycle: BillingCycle, anchorDay?: number | null): number {
  const end = toKstDate(currentEndMs);
  return kstDayEndMs(addMonthsKst(end, -monthsOf(cycle), validAnchor(anchorDay) ?? end.day));
}

/** 이번 주기의 일수 — 직전 종료일부터 이번 종료일까지 KST 날짜 차 */
export function periodDays(currentEndMs: number, cycle: BillingCycle, anchorDay?: number | null): number {
  return kstDayIndex(currentEndMs) - kstDayIndex(previousPeriodEndMs(currentEndMs, cycle, anchorDay));
}

/** `firstPaidPeriod` 가 읽는 지금 구독 — 워크스페이스 문서의 `subscriptionPlan` 에서 */
export type CurrentSubscriptionPeriod = {
  planId: string | null | undefined;
  planStatus: string | null | undefined;
  planEndMs: number | null;
};

/** 첫 결제에 얹는 보너스의 갈래 — 체험 중 결제 · 만료 뒤 다시 결제 */
export type PaymentBonusType = 'trial' | 'winback';

export type FirstPaidPeriod = {
  /** 플랜 종료 시각(KST 23:59:59) */
  endMs: number;
  /** 갱신 기준일(`billingAnchorDay`) — 체험 보너스면 종료일의 «일», 아니면 결제일의 «일» */
  anchorDay: number;
  /** 체험 보너스가 얹혔다(`bonusType === 'trial'`) */
  onTrial: boolean;
  /** 얹은 무료 달 수(`TRIAL_PAY_BONUS_MONTHS`) — 보너스가 없으면 0 */
  bonusMonths: number;
  /** 얹은 체험 일수 — 체험 보너스가 아니면 0 · 체험 종료일 당일도 0 */
  carriedDays: number;
  /** 얹은 보너스의 갈래 — 없으면 `null`. 서버는 이 값이 있을 때만 결제 내역에 `bonus` 를 적는다 */
  bonusType: PaymentBonusType | null;
};

/** 첫 결제 보너스로 더 주는 무료 달 수 — 체험 중 결제와 만료 뒤 결제가 같다 */
export const TRIAL_PAY_BONUS_MONTHS = 1;

/**
 * 첫 결제에 얹을 보너스의 갈래 — 워크스페이스당 한 번(`bonusUsed`)이다.
 * 체험 중 = `plan_trial` · 만료 표시 없음 · 종료 시각 > 지금. 만료 = 구독이 있고(planId) 만료 표시가 있거나 종료 시각 ≤ 지금.
 * 종료 시각을 못 읽고 만료 표시도 없으면 보너스가 없다.
 * @param bonusUsed 그 워크스페이스의 결제 내역 중 `bonus` 가 적힌 것이 있는가
 */
export function paymentBonusTypeOf(
  current: CurrentSubscriptionPeriod | null | undefined,
  nowMs: number,
  bonusUsed: boolean
): PaymentBonusType | null {
  if (bonusUsed || !current || !current.planId) return null;
  const markedExpired = current.planStatus === 'expired';
  if (current.planId === 'plan_trial' && !markedExpired && current.planEndMs !== null && current.planEndMs > nowMs) {
    return 'trial';
  }
  if (markedExpired || (current.planEndMs !== null && current.planEndMs <= nowMs)) return 'winback';
  return null;
}

/**
 * 첫 결제의 기간 — 오늘 + 한 주기. 보너스를 처음 받는 워크스페이스면 무료 1달을 더하고,
 * 체험 중이면 남은 체험 일수(`remainingDays`)도 더한다(`paymentBonusTypeOf`).
 * 서버(플랜 종료일·결제창 상품명)와 결제 화면 만료일·요금 화면 혜택 카드가 같은 답을 내도록 한 곳에 둔다.
 * @param bonusUsed 그 워크스페이스가 이미 보너스를 받았는가 — 받았으면 체험 중이어도 오늘 + 한 주기다
 */
export function firstPaidPeriod(
  current: CurrentSubscriptionPeriod | null | undefined,
  nowMs: number,
  cycle: BillingCycle,
  bonusUsed: boolean
): FirstPaidPeriod {
  const bonusType = paymentBonusTypeOf(current, nowMs, bonusUsed);
  const today = toKstDate(nowMs);

  if (bonusType === null) {
    return { endMs: firstPeriodEndMs(nowMs, cycle), anchorDay: today.day, onTrial: false, bonusMonths: 0, carriedDays: 0, bonusType };
  }

  const bonusEnd = addMonthsKst(today, monthsOf(cycle) + TRIAL_PAY_BONUS_MONTHS, today.day);
  if (bonusType === 'winback') {
    return { endMs: kstDayEndMs(bonusEnd), anchorDay: today.day, onTrial: false, bonusMonths: TRIAL_PAY_BONUS_MONTHS, carriedDays: 0, bonusType };
  }

  const carriedDays = remainingDays(current!.planEndMs!, nowMs);
  const endMs = kstDayEndMs(bonusEnd) + carriedDays * DAY_MS;
  return { endMs, anchorDay: toKstDate(endMs).day, onTrial: true, bonusMonths: TRIAL_PAY_BONUS_MONTHS, carriedDays, bonusType };
}

/**
 * 결제 내역·결제창 상품명의 기간 줄 — 「2026.10.13 ~ 2026.12.13」(KST).
 * @param endMs 플랜 종료 시각(`firstPaidPeriod`)
 */
export function planPeriodLabel(startMs: number, endMs: number): string {
  return `${dottedKstDate(startMs)} ~ ${dottedKstDate(endMs)}`;
}

/** KST 날짜 「2026.04.20」 */
export function dottedKstDate(ms: number): string {
  const { year, month, day } = toKstDate(ms);
  return `${year}.${pad2(month)}.${pad2(day)}`;
}

/**
 * 남은 일수 — 오늘(KST)부터 종료일까지 날짜 차. 오늘이 종료일이면 0 · 이미 지났으면 0.
 * @param nowMs 지금 시각
 */
export function remainingDays(currentEndMs: number, nowMs: number): number {
  return Math.max(0, kstDayIndex(currentEndMs) - kstDayIndex(nowMs));
}

// ---- private ------------------------------------------------------------

function monthsOf(cycle: BillingCycle): number {
  return cycle === 'yearly' ? 12 : 1;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** KST 날짜를 1970-01-01 부터 센 일 번호로 */
function kstDayIndex(ms: number): number {
  return Math.floor((ms + KST_OFFSET_MS) / DAY_MS);
}

function validAnchor(anchorDay: number | null | undefined): number | null {
  const n = Number(anchorDay);
  return Number.isInteger(n) && n >= 1 && n <= 31 ? n : null;
}
